import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button, Icon, Pill, Stack, Text } from '@/design-system'
import { useRepository } from '@/data/repository-context'
import { relativeTime } from '@/lib/relative-time'
import { cn } from '@/lib/cn'
import type { AppNotification } from '@/data/types'
import styles from './NotificationsPanel.module.css'

export function NotificationsPanel() {
  const repository = useRepository()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<readonly AppNotification[]>([])
  const wrapRef = useRef<HTMLDivElement>(null)

  const load = useCallback(() => {
    repository
      .listNotifications()
      .then(setItems)
      .catch(() => {
        /* ambient: keep whatever we already showed */
      })
  }, [repository])

  useEffect(() => load(), [load])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const unread = items.filter((item) => !item.read).length

  const markAllRead = async () => {
    await repository.markNotificationsRead()
    load()
  }

  const openItem = (item: AppNotification) => {
    setOpen(false)
    if (item.to) navigate(item.to)
  }

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={styles.bell}
        onClick={() => setOpen((value) => !value)}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Icon name="notifications" size={18} />
        {unread > 0 && <span className={styles.count}>{unread}</span>}
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Notifications">
          <div className={styles.head}>
            <Stack direction="row" align="center" gap={2}>
              <Text variant="caption" weight="semibold">
                Notifications
              </Text>
              {unread > 0 && <Pill tone="accent">{unread} new</Pill>}
            </Stack>
            {unread > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllRead}>
                Mark all read
              </Button>
            )}
          </div>

          <div className={styles.list}>
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={cn(styles.item, !item.read && styles.unreadItem)}
                onClick={() => openItem(item)}
              >
                <span className={styles.glyph}>
                  <Icon name={item.icon} size={18} />
                </span>
                <Stack grow gap={1}>
                  <Text variant="caption" weight="semibold">
                    {item.title}
                  </Text>
                  <Text variant="caption" tone="secondary">
                    {item.body}
                  </Text>
                  <Text variant="eyebrow" tone="tertiary">
                    {relativeTime(item.at)}
                  </Text>
                </Stack>
              </button>
            ))}
          </div>

          <div className={styles.foot}>
            <Button
              variant="outline"
              size="sm"
              block
              iconEnd="arrow_forward"
              onClick={() => {
                setOpen(false)
                navigate('/notifications')
              }}
            >
              See all notifications
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
